import { type TypedDocumentNode, gql } from '@apollo/client';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, HeadlineL } from '@sorare/core/src/atoms/typography';
import { ConfirmDialog } from '@sorare/core/src/components/form/ConfirmDialog';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { glossary } from '@sorare/core/src/lib/glossary';

import { useWatchlists } from 'hooks/watchlist/useWatchlists';

import type { DestroyWatchlistDialog_watchlist } from './__generated__/index.graphql';

const ModalContent = styled(Vertical)`
  padding: var(--double-unit);
`;

type Props = {
  watchlist: DestroyWatchlistDialog_watchlist;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export const DestroyWatchlistDialog = ({
  open,
  watchlist,
  onClose,
  onSuccess,
}: Props) => {
  const [watchlistTitle] = useState(watchlist.title);
  const { showNotification } = useSnackNotificationContext();
  const { formatMessage } = useIntlContext();
  const { destroyWatchlist } = useWatchlists();
  const onConfirm = async () => {
    await destroyWatchlist.mutate(watchlist.id);
    onSuccess();
    showNotification('watchlistDestroyed', {
      title: watchlistTitle,
    });
  };
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      cta={formatMessage(glossary.delete)}
      message={
        <ModalContent center className="text-center" gap={2}>
          <FontAwesomeIcon icon={faTrash} size="2x" />
          <HeadlineL>
            <FormattedMessage
              id="DestroyWatchlist.modal.title"
              defaultMessage="Delete watchlist “{title}”"
              values={{
                title: watchlist.title,
              }}
            />
          </HeadlineL>
          <BodyM color="var(--c-nd-600)">
            <FormattedMessage
              id="DestroyWatchlist.modal.description"
              defaultMessage="Are you sure you want to delete this watchlist? This cannot be undone"
            />
          </BodyM>
        </ModalContent>
      }
      onConfirm={onConfirm}
      fullScreen={false}
      ctaProps={{ color: 'red' }}
    />
  );
};

DestroyWatchlistDialog.fragments = {
  watchlist: gql`
    fragment DestroyWatchlistDialog_watchlist on Watchlist {
      id
      title
    }
  ` as TypedDocumentNode<DestroyWatchlistDialog_watchlist>,
};
